import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Coins, Check } from "lucide-react";
import { toast } from "sonner";
import { formatWIB } from "@/lib/datetime";
import StickyHeader from "@/components/StickyHeader";

const parseRp = (s) => Number(String(s).replace(/[^\d]/g, "")) || 0;
const fmtRp = (n) => Number(n || 0).toLocaleString("id-ID");

export default function PengaturanTarif() {
    const [rows, setRows] = useState([]);
    const [draft, setDraft] = useState({}); // prodi_id -> string
    const [savingId, setSavingId] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/tarif-unit-cost");
            setRows(data);
            setDraft(Object.fromEntries(data.map((r) => [r.prodi_id, String(r.tarif_rupiah || 0)])));
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const save = async (row) => {
        const val = parseRp(draft[row.prodi_id]);
        setSavingId(row.prodi_id);
        try {
            await api.put("/tarif-unit-cost", { prodi_id: row.prodi_id, tarif_rupiah: val });
            toast.success(`Tarif ${row.nama_prodi} disimpan`);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setSavingId(null);
        }
    };

    const totalTerisi = useMemo(() => rows.filter((r) => r.tarif_rupiah > 0).length, [rows]);
    // Baris yg tarifnya belum pernah diatur (updated_at null) ditaruh paling atas
    // supaya UFP gampang menemukan prodi yg belum diisi (termasuk prodi Koas baru).
    const sortedRows = useMemo(
        () => [...rows].sort((a, b) => (a.updated_at ? 1 : 0) - (b.updated_at ? 1 : 0)),
        [rows]
    );

    return (
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-8 space-y-4">
            <StickyHeader>
            <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Pengaturan</div>
                <h1 className="text-3xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Plus Jakarta Sans" }}>
                    Tarif Unit Cost
                </h1>
                <p className="text-sm text-slate-500 mt-1">
                    Tarif unit cost (per bulan, 100%) untuk tiap Program Studi. Dipakai Laporan Unit Cost:
                    hari Stase RSCM 1–14 → 50% tarif, &gt;14 → 100% tarif.
                </p>
                <p className="text-sm text-amber-700 mt-1">
                    Tarif harus diisi manual untuk tiap prodi (termasuk Koas) sebelum Laporan Unit Cost
                    dipakai untuk penagihan — prodi yang belum diisi akan muncul Rp 0 di laporan.
                </p>
            </div>
            </StickyHeader>

            <Card className="border-slate-200 shadow-sm overflow-hidden">
                <div className="px-4 py-2 border-b bg-slate-50 text-xs text-slate-500 flex items-center gap-1.5">
                    <Coins className="w-3.5 h-3.5" /> {totalTerisi} dari {rows.length} prodi sudah punya tarif
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full compact">
                        <thead className="bg-slate-100">
                            <tr className="text-left text-xs uppercase tracking-wider text-slate-600 font-semibold">
                                <th>Program Studi</th><th>Jenjang</th><th>Tarif Unit Cost (Rp)</th>
                                <th>Terakhir Diubah</th><th className="text-right pr-4">Aksi</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={5} className="text-center py-10 text-slate-400">Memuat...</td></tr>
                            ) : sortedRows.map((r) => {
                                const changed = parseRp(draft[r.prodi_id]) !== (r.tarif_rupiah || 0);
                                return (
                                    <tr key={r.prodi_id} className="border-t border-slate-200 hover:bg-slate-50">
                                        <td className="font-semibold text-slate-800">{r.nama_prodi}</td>
                                        <td><Badge variant="outline" className="text-[10px]">{r.jenjang}</Badge></td>
                                        <td>
                                            <div className="flex items-center gap-1.5">
                                                <span className="text-slate-400 text-xs">Rp</span>
                                                <Input
                                                    className="h-8 w-40 text-right font-mono"
                                                    value={fmtRp(parseRp(draft[r.prodi_id] ?? "0"))}
                                                    onChange={(e) => setDraft({ ...draft, [r.prodi_id]: e.target.value })}
                                                    data-testid={`tarif-input-${r.prodi_id}`}
                                                />
                                            </div>
                                        </td>
                                        <td className="text-[11px] text-slate-500">
                                            {r.updated_at
                                                ? <>{formatWIB(r.updated_at)}<div className="text-slate-400">{r.updated_by}</div></>
                                                : <Badge className="text-[9px] bg-amber-100 text-amber-700 border-amber-300" variant="outline">Belum diisi</Badge>}
                                        </td>
                                        <td className="text-right pr-2">
                                            <Button
                                                size="sm"
                                                disabled={!changed || savingId === r.prodi_id}
                                                onClick={() => save(r)}
                                                className="bg-[#00B9AD] hover:bg-[#60C0D0]"
                                                data-testid={`tarif-save-${r.prodi_id}`}
                                            >
                                                <Check className="w-3.5 h-3.5 mr-1" />
                                                {savingId === r.prodi_id ? "..." : "Simpan"}
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </Card>
        </div>
    );
}
